import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  StockSearchSuggestion,
  searchStockSuggestions,
} from '../../api/stockSearchApi';

const TXT = '#111827';
const SUB = '#5f6b7a';
const SUB2 = '#8a95a5';
const BORDER = '1px solid #dbe3ec';
const CARD = '#f8fafc';
const GOOD = '#047857';
const RED = '#b91c1c';
const AMBER = '#a36a00';

export type { StockSearchSuggestion };

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSelect: (stock: StockSearchSuggestion) => void;
  onEnter?: () => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  limit?: number;
  minLength?: number;
  inputSx?: SxProps<Theme>;
  selectedValueMode?: 'ticker' | 'name' | 'name-ticker';
  selectFirstOnEnter?: boolean;
};

function formatNumber(value: any, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('ko-KR', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function formatWon(value: any) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '-';
  if (n >= 1_0000_0000_0000) return `${formatNumber(n / 1_0000_0000_0000, 1)}조`;
  if (n >= 1_0000_0000) return `${formatNumber(n / 1_0000_0000, 0)}억`;
  if (n >= 1_0000) return `${formatNumber(n / 1_0000, 0)}만`;
  return formatNumber(n);
}

function formatPct(value: any) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n > 0 ? '+' : ''}${formatNumber(n, 2)}%`;
}

function toneSx(label?: string | null, tone?: string | null) {
  const value = String(label || tone || '').toLowerCase();
  if (value.includes('risk') || value.includes('위험')) {
    return { bgcolor: '#fee2e2', color: RED, border: '1px solid #fecaca' };
  }
  if (value.includes('buy') || value.includes('매수') || value.includes('우량') || value.includes('review') || value.includes('관찰')) {
    return { bgcolor: '#dcfce7', color: GOOD, border: '1px solid #bbf7d0' };
  }
  if (value.includes('wait') || value.includes('대기') || value.includes('보류')) {
    return { bgcolor: '#fff7ed', color: AMBER, border: '1px solid #fed7aa' };
  }
  return { bgcolor: '#eef2f7', color: SUB, border: BORDER };
}

function selectedInputValue(
  stock: StockSearchSuggestion,
  mode: 'ticker' | 'name' | 'name-ticker',
) {
  if (mode === 'ticker') return stock.ticker || stock.corpName || '';
  if (mode === 'name') return stock.corpName || stock.ticker || '';
  return `${stock.corpName}${stock.ticker ? ` ${stock.ticker}` : ''}`.trim();
}

export default function StockSearchSuggestInput({
  value,
  onChange,
  onSelect,
  onEnter,
  label,
  placeholder = '종목명 또는 6자리 코드 입력',
  size = 'small',
  disabled = false,
  limit = 10,
  minLength = 1,
  inputSx,
  selectedValueMode = 'name-ticker',
  selectFirstOnEnter = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<StockSearchSuggestion[]>([]);
  const [error, setError] = useState('');
  const blurTimer = useRef<number | null>(null);
  const requestSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const composingRef = useRef(false);
  const [isComposing, setIsComposing] = useState(false);

  // 후보를 클릭한 직후 부모 컴포넌트가 value를 ticker 또는 "종목명 ticker"로 갱신하면
  // effect가 다시 실행되면서 목록이 재오픈될 수 있다. 사용자가 다시 타이핑하기 전까지는
  // 자동완성 목록을 의도적으로 닫아 둔다.
  const suppressSuggestUntilTyping = useRef(false);

  const keyword = useMemo(() => String(value || '').trim(), [value]);

  useEffect(() => {
    if (isComposing) {
      setOpen(false);
      setLoading(false);
      return;
    }

    if (suppressSuggestUntilTyping.current) {
      setOpen(false);
      setLoading(false);
      setError('');
      setItems([]);
      return;
    }

    if (disabled || keyword.length < minLength) {
      setItems([]);
      setError('');
      setLoading(false);
      setOpen(false);
      return;
    }

    const seq = ++requestSeq.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const rows = await searchStockSuggestions(keyword, limit);
        if (requestSeq.current !== seq || suppressSuggestUntilTyping.current) return;
        setItems(rows);
        setOpen(focused && rows.length > 0);
      } catch (e: any) {
        if (requestSeq.current !== seq || suppressSuggestUntilTyping.current) return;
        setItems([]);
        setError(e?.response?.data?.message || e?.message || '종목 검색 중 오류가 발생했습니다.');
        setOpen(focused);
      } finally {
        if (requestSeq.current === seq) setLoading(false);
      }
    }, 220);

    return () => window.clearTimeout(timer);
  }, [disabled, focused, isComposing, keyword, limit, minLength]);

  const closeSuggest = useCallback(() => {
    requestSeq.current += 1;
    setOpen(false);
    setFocused(false);
    setItems([]);
    setError('');
    setLoading(false);
  }, []);

  const handleSelect = useCallback((stock: StockSearchSuggestion) => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);

    // 한글 조합 중 후보를 클릭하면 브라우저가 늦은 compositionend/onChange를
    // 한 번 더 발생시킬 수 있다. 선택 직전부터 입력 이벤트를 잠가서
    // "000660ㅅ"처럼 선택 ticker 뒤에 마지막 자모가 붙는 현상을 막는다.
    suppressSuggestUntilTyping.current = true;
    composingRef.current = false;
    setIsComposing(false);
    closeSuggest();

    onChange(selectedInputValue(stock, selectedValueMode));
    onSelect(stock);

    window.setTimeout(() => {
      inputRef.current?.blur();
    }, 0);
  }, [closeSuggest, onChange, onSelect, selectedValueMode]);

  return (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <TextField
        fullWidth
        size={size}
        label={label}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        inputRef={inputRef}
        onFocus={() => {
          setFocused(true);
          if (!suppressSuggestUntilTyping.current && (items.length || error)) setOpen(true);
        }}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => {
            setFocused(false);
            setOpen(false);
          }, 140);
        }}
        onCompositionStart={() => {
          composingRef.current = true;
          setIsComposing(true);
          // 선택 이후 실제 사용자가 새 한글 입력을 시작한 경우에는 잠금을 해제한다.
          suppressSuggestUntilTyping.current = false;
        }}
        onCompositionEnd={() => {
          composingRef.current = false;
          setIsComposing(false);

          // MUI TextField의 composition event currentTarget은 HTMLDivElement로 추론될 수 있다.
          // 실제 입력값은 inputRef에서 읽어야 타입과 런타임 모두 안전하다.
          if (suppressSuggestUntilTyping.current) return;

          setFocused(true);
          onChange(inputRef.current?.value ?? '');
          setOpen(true);
        }}
        onChange={(e) => {
          // 후보 선택 뒤 브라우저가 밀어 넣는 잔여 IME change 이벤트는 버린다.
          if (suppressSuggestUntilTyping.current) return;

          setFocused(true);
          onChange(e.target.value);
          if (!composingRef.current) setOpen(true);
        }}
        onKeyDown={(e) => {
          // 선택 완료 뒤 사용자가 새로 키를 누른 경우에만 입력 잠금을 해제한다.
          if (
            suppressSuggestUntilTyping.current &&
            (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')
          ) {
            suppressSuggestUntilTyping.current = false;
          }

          if (e.key === 'Enter') {
            if (
              selectFirstOnEnter &&
              items.length > 0 &&
              !suppressSuggestUntilTyping.current &&
              !composingRef.current
            ) {
              e.preventDefault();
              handleSelect(items[0]);
              return;
            }
            setOpen(false);
            onEnter?.();
          }
          if (e.key === 'Escape') {
            suppressSuggestUntilTyping.current = true;
            closeSuggest();
          }
        }}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={15} /> : undefined,
        }}
        sx={inputSx}
      />

      {open ? (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            zIndex: 40,
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            maxHeight: 330,
            overflowY: 'auto',
            borderRadius: 2,
            border: BORDER,
            bgcolor: '#fff',
          }}
        >
          {error ? (
            <Typography sx={{ p: 1.2, color: RED, fontSize: 12, fontWeight: 800 }}>{error}</Typography>
          ) : items.length ? (
            items.map((stock) => {
              const pct = Number(stock.priceChangePct);
              return (
                <Box
                  key={stock.ticker}
                  onMouseDown={(e) => {
                    // input blur/composition commit보다 먼저 선택 잠금을 건다.
                    e.preventDefault();
                    suppressSuggestUntilTyping.current = true;
                    composingRef.current = false;
                    setIsComposing(false);
                  }}
                  onClick={() => handleSelect(stock)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto' },
                    gap: 0.8,
                    alignItems: 'center',
                    px: 1.15,
                    py: 0.9,
                    cursor: 'pointer',
                    borderBottom: BORDER,
                    '&:hover': { bgcolor: CARD },
                    '&:last-of-type': { borderBottom: 0 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: TXT, fontSize: 13.2, fontWeight: 950 }} noWrap>
                      {stock.corpName} <Typography component="span" sx={{ color: SUB, fontSize: 12, fontWeight: 850 }}>{stock.ticker}</Typography>
                    </Typography>
                    <Typography sx={{ color: SUB2, fontSize: 11.2, mt: 0.15 }} noWrap>
                      {stock.market || '-'} · 현재가 {formatNumber(stock.closePrice)} · 거래대금 {formatWon(stock.tradingValue)}
                    </Typography>
                  </Box>

                  <Typography sx={{ color: Number.isFinite(pct) && pct >= 0 ? GOOD : RED, fontSize: 12.2, fontWeight: 950, display: { xs: 'none', sm: 'block' } }}>
                    {formatPct(stock.priceChangePct)}
                  </Typography>

                  <Chip
                    size="small"
                    label={stock.decisionLabel || '선택'}
                    sx={{ height: 24, fontSize: 11, fontWeight: 900, ...toneSx(stock.decisionLabel, stock.decisionTone) }}
                  />
                </Box>
              );
            })
          ) : keyword.length >= minLength && !loading ? (
            <Typography sx={{ p: 1.2, color: SUB, fontSize: 12, fontWeight: 800 }}>검색된 종목이 없습니다.</Typography>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
}
